/**
 * Checkout — picker zapisanych adresów (karty do kliknięcia).
 *
 * Obsługuje DOWOLNĄ liczbę pickerów na stronie (B2C billing, B2B faktura + dostawa).
 * Każdy wrapper `[data-hn-co-address-picker]` konfiguruje się atrybutami:
 *   - data-field-prefix="billing|shipping"  (domyślnie billing) — które pola wypełnić
 *   - data-address-type="invoice|shipping"   (opcjonalnie) — filtr typu adresu
 *
 * Adresy pobierane raz z REST `/hn/v1/account/addresses` i współdzielone między pickery.
 * Wybór karty → fill pól `${prefix}_*` + trigger change (shipping refresh / validate).
 */

const REST_PATH = 'hn/v1/account/addresses';

function getRestBase() {
    if (window.wpApiSettings && window.wpApiSettings.root) return window.wpApiSettings.root;
    return '/wp-json/';
}
function authHeaders() {
    const h = { 'Content-Type': 'application/json' };
    if (window.wpApiSettings && window.wpApiSettings.nonce) h['X-WP-Nonce'] = window.wpApiSettings.nonce;
    return h;
}

/* address field (REST) → suffix pola formularza (prefiksowane prefixem pickera). */
const FIELD_SUFFIXES = ['first_name', 'last_name', 'company', 'nip', 'address_1', 'postcode', 'city', 'country', 'phone'];

function setField(prefix, suffix, value) {
    const el = document.getElementById(`${prefix}_${suffix}`);
    if (!el) return;
    const v = value || '';
    if (el.tagName === 'SELECT') { if (v) el.value = v; }
    else el.value = v;
    if (suffix === 'city' && v) el.dataset.hnCoCityUserTyped = '1'; // anti-overwrite dla postcode-lookup
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
}

function fillFields(prefix, addr) {
    // Adres z NIP-em → zaznacz „Chcę fakturę”, inaczej pola zostają disabled i nie idą w POST.
    const invoice = document.querySelector('input[name="hn_want_invoice"]');
    if (prefix === 'billing' && addr.nip && invoice && !invoice.checked) {
        invoice.checked = true;
        invoice.dispatchEvent(new Event('change', { bubbles: true }));
    }
    FIELD_SUFFIXES.forEach((s) => setField(prefix, s, addr[s]));
}

function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

function typeLabel(t) {
    if (t === 'shipping') return 'Dostawa';
    if (t === 'invoice') return 'Faktura';
    return '';
}

function cardHtml(a) {
    const name = [a.first_name, a.last_name].filter(Boolean).join(' ');
    const loc = [a.postcode, a.city].filter(Boolean).join(' ');
    const pills = [];
    if (a.is_primary) pills.push('<span class="hn-co__addr-card-pill hn-co__addr-card-pill--primary">Domyślny</span>');
    const tl = typeLabel(a.type);
    if (tl) pills.push(`<span class="hn-co__addr-card-pill">${esc(tl)}</span>`);
    return `
        <button type="button" class="hn-co__addr-card" data-addr-id="${esc(a.id)}" aria-pressed="false">
            <span class="hn-co__addr-card-head">
                <strong class="hn-co__addr-card-label">${esc(a.label || 'Adres')}</strong>
                ${pills.join('')}
            </span>
            <span class="hn-co__addr-card-body">
                ${name ? esc(name) + '<br>' : ''}
                ${a.company ? esc(a.company) + '<br>' : ''}
                ${a.nip ? 'NIP: ' + esc(a.nip) + '<br>' : ''}
                ${esc(a.address_1 || '')}${loc ? '<br>' + esc(loc) : ''}
            </span>
        </button>`;
}

function renderPicker(picker, addresses) {
    const prefix = picker.dataset.fieldPrefix || 'billing';
    const cardsWrap = picker.querySelector('[data-hn-co-address-cards]');
    if (!cardsWrap) return;

    cardsWrap.innerHTML = addresses.map(cardHtml).join('');
    picker.hidden = false;

    const cards = Array.from(cardsWrap.querySelectorAll('.hn-co__addr-card'));
    function select(card, addr, doFill) {
        cards.forEach((c) => { c.classList.remove('is-selected'); c.setAttribute('aria-pressed', 'false'); });
        card.classList.add('is-selected');
        card.setAttribute('aria-pressed', 'true');
        if (doFill) fillFields(prefix, addr);
    }
    cards.forEach((card) => {
        const addr = addresses.find((a) => String(a.id) === card.dataset.addrId);
        if (!addr) return;
        card.addEventListener('click', () => select(card, addr, true));
    });

    /* Auto-zaznacz domyślny gdy pola prefixu puste (nie nadpisuj jeśli user już coś wpisał).
     * Tylko gdy adres jest oznaczony jako domyślny — bez domyślnego nie zgadujemy. */
    const filled = (document.getElementById(`${prefix}_address_1`) || {}).value?.trim();
    if (!filled) {
        const primary = addresses.find((a) => a.is_primary);
        const card = primary && cards.find((c) => c.dataset.addrId === String(primary.id));
        if (card) select(card, primary, true);
    }
}

let _cache = null;
async function fetchAddresses() {
    if (_cache) return _cache;
    const res = await fetch(getRestBase() + REST_PATH, { credentials: 'same-origin', headers: authHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    _cache = data.addresses || [];
    return _cache;
}

function filterByType(picker, addresses) {
    const want = picker.dataset.addressType;
    if (!want) return addresses;
    return addresses.filter((a) => !a.type || a.type === 'both' || a.type === want);
}

export async function initCheckoutAddressPicker() {
    const pickers = Array.from(document.querySelectorAll('[data-hn-co-address-picker]'));
    if (!pickers.length) return;

    let addresses;
    try {
        addresses = await fetchAddresses();
    } catch (_) { return; } // silent — pickery się nie pokażą
    if (!addresses.length) return;

    pickers.forEach((picker) => {
        const list = filterByType(picker, addresses);
        if (list.length) renderPicker(picker, list);
    });
}
